import { MetricGrouping, MetricIntervalPeriod } from '@/types/agentEvents';
import {
  defaultHourRange,
  defaultOccupancyParams,
} from '@/utils/metricsParams';
import { meanMetricValue, formatPercentage } from '@/utils/metricsAggregate';
import { MetricsSection } from './MetricsSection';
import { IntervalMetricsChartWithSeries } from '../common/IntervalMetricsChartWithSeries';
import { MetricCard } from '../common/MetricCard';
import { api } from '@/store/api';
import { Box } from '@gapartners/ui';
import { useTranslation } from 'react-i18next';

const { useGetOccupancyMetricsQuery } = api;

const occupancyValue = (value: number | string): number => {
  if (typeof value === 'number') return value;
  const n = parseFloat(String(value).replace(/%/g, ''));
  return Number.isFinite(n) ? n : 0;
};

export function AgentOccupancySection() {
  const { t } = useTranslation();

  return (
    <MetricsSection
      titleKey="components.dashboard.agentOccupancy"
      descriptionKey="components.dashboard.agentOccupancyDescription"
      metricName="occupancy"
      defaultParams={{
        ...defaultOccupancyParams(),
        ...defaultHourRange(),
        groupings: [MetricGrouping.AGENT],
        intervalPeriod: MetricIntervalPeriod.HOUR,
      }}
      useQuery={useGetOccupancyMetricsQuery}
      renderData={({ data, params, metricValue, groupingKey }) => (
        <Box>
          {/* Team Average Card */}
          <Box sx={{ mb: 2 }}>
            <MetricCard
              title={t('components.dashboard.teamAverage')}
              value={formatPercentage(meanMetricValue(data, occupancyValue))}
              suffix="%"
            />
          </Box>

          {/* Chart or Grid */}
          {params.intervalPeriod !== MetricIntervalPeriod.TOTAL &&
          data.some(item => item.intervalStartTime != null) ? (
            <IntervalMetricsChartWithSeries
              data={data}
              groupingKey={groupingKey}
              valueFormatter={v => `${metricValue(v)}%`}
            />
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                  xl: 'repeat(5, 1fr)',
                },
                gap: 2,
              }}
            >
              {data.map(item => (
                <MetricCard
                  key={item.id}
                  title={item.dimensionLabels?.[groupingKey] ?? item.name}
                  value={metricValue(item.value)}
                  max={100}
                  suffix="%"
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    />
  );
}
